import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  ArrowLeft, Tv, Users, CheckCircle2, XCircle, Ban, ShieldCheck, Plus,
  UserPlus, Trash2, Clock, LogOut, ExternalLink,
} from "lucide-react";
import { toast } from "sonner";
import EpisodeSignoffLedger from "@/pages/tv/EpisodeSignoffLedger";
import ScriptChangeLog from "@/pages/tv/ScriptChangeLog";
import CastApprovalLedger from "@/pages/tv/CastApprovalLedger";

const ROLE_LABELS = {
  programming_head:        "Programming Head",
  show_manager:            "Show Manager",
  creative_supervisor:     "Creative Supervisor",
  standards_and_practices: "Standards & Practices",
  executive:               "Executive",
};

const ROLE_COLORS = {
  programming_head:        "text-emerald-300 bg-emerald-500/15",
  show_manager:            "text-sky-300 bg-sky-500/15",
  creative_supervisor:     "text-fuchsia-300 bg-fuchsia-500/15",
  standards_and_practices: "text-amber-300 bg-amber-500/15",
  executive:               "text-white/70 bg-white/10",
};

// Read-mostly cubicle. Header pulls project snapshot + desk metadata.
// Members panel supports invite / role-change / remove for any active
// desk member (self-propagating spread — chosen by the user).
// PH-owner viewer sees an Approve/Reject action bar when desk is pending.
export default function ShowDeskDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [decisionOpen, setDecisionOpen] = useState(null); // 'approve'|'reject'|null

  const load = async () => {
    setLoading(true);
    try {
      const { data: resp } = await api.get(`/show-desks/${id}`);
      setData(resp);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Not accessible");
      nav("/channel/show-desks");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { void load(); }, [id]); // eslint-disable-line

  if (loading || !data) {
    return (
      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-16 text-center text-white/55">
        Loading show desk…
      </div>
    );
  }

  const { desk, members, project, viewer_role } = data;
  const canInvite = ["admin", "channel_owner", "desk_member"].includes(viewer_role);
  const canDecide = ["admin", "ph_owner"].includes(viewer_role)
    && desk.status === "requested";
  const canRevoke = ["admin", "channel_owner", "ph_owner"].includes(viewer_role)
    && desk.status === "active";

  const removeMember = async (memberId) => {
    if (!confirm("Remove this member from the desk?")) return;
    try {
      await api.delete(`/show-desks/${id}/members/${memberId}`);
      toast.success("Member removed");
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not remove");
    }
  };

  const revoke = async () => {
    if (!confirm("Revoke this Show Desk? All members lose access immediately.")) return;
    try {
      await api.post(`/show-desks/${id}/revoke`);
      toast.success("Desk revoked");
      nav("/channel/show-desks");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not revoke");
    }
  };

  return (
    <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-8"
      data-testid="show-desk-detail-page">
      <button onClick={() => nav("/channel/show-desks")}
        className="text-[10px] tracking-[0.25em] uppercase text-white/55 hover:text-white inline-flex items-center gap-1.5 mb-3"
        data-testid="desk-back-btn">
        <ArrowLeft size={12} /> Back to Show Desks
      </button>

      {/* Hero */}
      <div className="flex items-start justify-between gap-6 flex-wrap">
        <div className="min-w-0">
          <div className="flex items-center gap-2.5 mb-1.5 flex-wrap">
            <span className="text-[10px] tracking-[0.3em] uppercase text-white/55">
              {desk.project_type?.replace(/_/g, " ")}
            </span>
            <StatusChip status={desk.status} />
            <span className={"text-[10px] tracking-[0.2em] uppercase px-2 py-0.5 rounded-full " +
              (viewer_role === "ph_owner" ? "bg-red-500/15 text-red-300"
              : viewer_role === "channel_owner" ? "bg-emerald-500/15 text-emerald-300"
              : viewer_role === "desk_member" ? "bg-sky-500/15 text-sky-300"
              : "bg-white/10 text-white/55")}
              data-testid="desk-viewer-role">
              {viewer_role.replace("_", " ")}
            </span>
          </div>
          <h1 className="font-display text-4xl sm:text-5xl uppercase tracking-tight"
            data-testid="desk-title">
            {desk.project_title}
          </h1>
          <div className="mt-2 text-sm text-white/55">
            Channel: {desk.channel_org_name} · PH: {desk.ph_org_name}
          </div>
        </div>
        <div className="flex gap-2">
          {canDecide && (
            <>
              <Button onClick={() => setDecisionOpen("approve")}
                className="btn-themed h-11 px-5 rounded-full text-xs uppercase tracking-[0.25em]"
                data-testid="desk-approve-btn">
                <CheckCircle2 size={14} className="mr-1.5" /> Approve
              </Button>
              <Button onClick={() => setDecisionOpen("reject")}
                variant="outline"
                className="h-11 px-5 border-red-500/40 text-red-300 hover:bg-red-500/10 rounded-full text-xs uppercase tracking-[0.25em]"
                data-testid="desk-reject-btn">
                <XCircle size={14} className="mr-1.5" /> Reject
              </Button>
            </>
          )}
          {canRevoke && (
            <Button onClick={revoke}
              variant="outline"
              className="h-11 px-5 border-white/15 text-white/70 hover:bg-white/5 rounded-full text-xs uppercase tracking-[0.25em]"
              data-testid="desk-revoke-btn">
              <Ban size={14} className="mr-1.5" /> Revoke desk
            </Button>
          )}
        </div>
      </div>

      {/* Requested / decision context */}
      {desk.status === "requested" && desk.requested_note && (
        <div className="mt-6 cs-card rounded-2xl p-5 border border-amber-500/25"
          data-testid="desk-requested-note">
          <div className="text-[10px] tracking-[0.28em] uppercase text-amber-300 mb-2">
            Message from the channel
          </div>
          <div className="text-sm text-white/85 whitespace-pre-line">
            {desk.requested_note}
          </div>
        </div>
      )}

      {desk.status === "rejected" && (
        <div className="mt-6 cs-card rounded-2xl p-5 border border-red-500/25"
          data-testid="desk-rejected-note">
          <div className="text-[10px] tracking-[0.28em] uppercase text-red-300 mb-2">
            Rejected by the Production House
          </div>
          <div className="text-sm text-white/70 whitespace-pre-line">
            {desk.decision_note || "No note provided."}
          </div>
          <div className="mt-3 text-[10px] tracking-[0.22em] uppercase text-white/45">
            Bind requests from this channel are blocked for 30 days from{" "}
            {desk.decided_at?.slice(0, 10)}.
          </div>
        </div>
      )}

      {/* Show snapshot — read-only mirror of project */}
      {desk.status === "active" && project && (
        <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4"
          data-testid="desk-show-snapshot">
          <SnapshotTile testid="desk-snap-format"
            label="Format" Icon={Tv} color="#0EA5E9"
            value={project.format_details?.format_name
                   || project.type_label
                   || "—"}
            sub={project.format_details?.telecast_slot || "Slot TBD"} />
          <SnapshotTile testid="desk-snap-aired-on"
            label="Aired on" Icon={ShieldCheck} color="#F5B82E"
            value={project.format_details?.aired_on_name || "—"}
            sub={project.format_details?.non_fiction_subtype
                 ? project.format_details.non_fiction_subtype.replace(/_/g, " ")
                 : ""} />
          <SnapshotTile testid="desk-snap-season"
            label="Season" Icon={Clock} color="#EC4899"
            value={project.format_details?.season_number
                   ? `S${project.format_details.season_number}`
                   : project.format_details?.current_episode_aired
                     ? `Ep ${project.format_details.current_episode_aired}`
                     : "—"}
            sub={project.format_details?.episodes_planned
                 ? `${project.format_details.episodes_planned} eps planned`
                 : project.format_details?.bank_target
                   ? `Bank +${project.format_details.bank_target}`
                   : ""} />
          <SnapshotTile testid="desk-snap-units"
            label="Shooting units" Icon={Users} color="#8B5CF6"
            value={String((project.additional_units || [])
                          .filter((u) => !u.deleted_at).length + 1)}
            sub={"Main unit + additional"} />
        </div>
      )}

      {/* Members */}
      <div className="mt-10" data-testid="desk-members-panel">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-white/50">
              Programming Team
            </div>
            <div className="text-sm text-white/55 mt-1">
              Any active member can invite peers to this desk.
            </div>
          </div>
          {canInvite && desk.status === "active" && (
            <Button onClick={() => setInviteOpen(true)}
              className="btn-themed h-10 px-5 rounded-full text-[10px] uppercase tracking-[0.22em]"
              data-testid="desk-invite-btn">
              <UserPlus size={13} className="mr-1.5" /> Invite exec
            </Button>
          )}
        </div>

        {members.length === 0 ? (
          <div className="cs-card rounded-2xl p-8 text-center text-white/55 text-sm">
            No members yet.
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-3">
            {members.map((m) => (
              <MemberRow key={m.id} m={m}
                canRemove={canInvite}
                onRemove={() => removeMember(m.id)} />
            ))}
          </div>
        )}
      </div>

      {/* Episode signoff ledger — only when desk is active */}
      {desk.status === "active" && (
        <div className="mt-10" data-testid="desk-episode-ledger-slot">
          <EpisodeSignoffLedger
            projectId={desk.project_id}
            viewerRole={viewer_role} />
        </div>
      )}

      {/* Script change log — only when desk is active */}
      {desk.status === "active" && (
        <div className="mt-10" data-testid="desk-script-log-slot">
          <ScriptChangeLog
            projectId={desk.project_id}
            viewerRole={viewer_role} />
        </div>
      )}

      {/* Cast approval ledger — only when desk is active */}
      {desk.status === "active" && (
        <div className="mt-10" data-testid="desk-cast-approval-slot">
          <CastApprovalLedger
            projectId={desk.project_id}
            viewerRole={viewer_role} />
        </div>
      )}

      {/* Open linked project link (owner + admin see this) */}
      {desk.status === "active" && (
        <div className="mt-10 text-center">
          <a href={`/p/${desk.project_id}`}
             className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.22em] text-white/55 hover:text-white"
             data-testid="desk-open-project-link">
            Open the linked project dashboard <ExternalLink size={11} />
          </a>
        </div>
      )}

      <InviteDialog
        open={inviteOpen}
        onOpenChange={setInviteOpen}
        deskId={desk.id}
        onInvited={load} />

      <DecisionDialog
        action={decisionOpen}
        onClose={() => setDecisionOpen(null)}
        deskId={desk.id}
        onDecided={load} />
    </div>
  );
}

function SnapshotTile({ testid, label, Icon, color, value, sub }) {
  return (
    <div className="cs-card rounded-2xl p-5 relative overflow-hidden"
      data-testid={testid}>
      <div className="absolute -top-8 -right-8 w-32 h-32 rounded-full opacity-15 blur-2xl pointer-events-none"
        style={{ background: color }} />
      <div className="relative flex items-center justify-between">
        <div className="w-9 h-9 rounded-lg flex items-center justify-center"
          style={{ background: `${color}24`, color }}>
          <Icon size={17} />
        </div>
        <div className="text-[9px] tracking-[0.24em] uppercase text-white/45">
          {label}
        </div>
      </div>
      <div className="mt-4 font-display text-xl uppercase tracking-tight text-white truncate">
        {value}
      </div>
      {sub && (
        <div className="mt-1 text-[10px] tracking-[0.18em] uppercase text-white/45 truncate">
          {sub}
        </div>
      )}
    </div>
  );
}

function StatusChip({ status }) {
  const map = {
    active:    { bg: "bg-emerald-500/15", fg: "text-emerald-300", label: "Active" },
    requested: { bg: "bg-amber-500/15",   fg: "text-amber-300",   label: "Pending" },
    rejected:  { bg: "bg-red-500/15",     fg: "text-red-300",     label: "Rejected" },
    revoked:   { bg: "bg-white/10",       fg: "text-white/55",    label: "Revoked" },
  };
  const s = map[status] || map.revoked;
  return (
    <span className={`px-2 py-0.5 rounded-full text-[10px] uppercase tracking-[0.2em] ${s.bg} ${s.fg}`}
      data-testid={`desk-status-${status}`}>
      {s.label}
    </span>
  );
}

function MemberRow({ m, canRemove, onRemove }) {
  return (
    <div className="cs-card rounded-2xl p-4 flex items-center gap-3"
      data-testid={`desk-member-row-${m.id}`}>
      <div className={"w-10 h-10 rounded-full flex items-center justify-center font-semibold text-sm " +
        (ROLE_COLORS[m.role] || ROLE_COLORS.executive)}>
        {(m.user_name || m.user_email || "?").slice(0, 1).toUpperCase()}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold text-white truncate">
          {m.user_name || m.user_email}
        </div>
        <div className="mt-0.5 text-[10px] tracking-[0.2em] uppercase text-white/55 truncate">
          {ROLE_LABELS[m.role] || m.role}
          {m.status === "invited" && <span className="ml-2 text-amber-300">· invite pending</span>}
        </div>
      </div>
      {canRemove && (
        <button onClick={onRemove}
          className="text-white/40 hover:text-red-300 transition-colors"
          data-testid={`desk-member-remove-${m.id}`}
          aria-label="Remove member">
          <Trash2 size={13} />
        </button>
      )}
    </div>
  );
}

function InviteDialog({ open, onOpenChange, deskId, onInvited }) {
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [role, setRole]   = useState("executive");
  const [note, setNote]   = useState("");
  const [saving, setSaving] = useState(false);

  const reset = () => { setEmail(""); setPhone(""); setRole("executive"); setNote(""); };

  const submit = async () => {
    if (!email.trim() && !phone.trim()) {
      toast.error("Enter an email or a mobile number");
      return;
    }
    setSaving(true);
    try {
      const body = { role, note: note.trim() };
      if (email.trim()) body.email = email.trim();
      if (phone.trim()) body.phone = phone.trim();
      const { data } = await api.post(`/show-desks/${deskId}/members`, body);
      const emailDry = data?.delivery?.email?.dry_run;
      const smsDry   = data?.delivery?.sms?.dry_run;
      if (emailDry || smsDry) {
        toast.success(
          "Exec invited — delivery is dry-run until Resend / SMS keys are set.",
        );
      } else {
        toast.success("Exec invited");
      }
      onOpenChange(false); reset();
      onInvited?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not invite");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) reset(); }}>
      <DialogContent className="bg-[#0a0a0c] border border-white/10 text-white max-w-lg"
        data-testid="desk-invite-dialog">
        <DialogHeader>
          <DialogTitle className="font-display uppercase tracking-tight text-lg">
            Invite an exec to this desk
          </DialogTitle>
          <DialogDescription className="text-white/55 text-xs">
            Provide an email, a mobile number, or both. We&apos;ll send a claim
            link so they can join even if they don&apos;t have a Call·Sheet
            account yet.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 mt-2">
          <div>
            <Label className="text-[10px] tracking-[0.22em] uppercase text-white/55">
              Corporate email
            </Label>
            <Input type="email" value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="programming.head@channel.tv"
              className="mt-2 h-11 bg-black/40 border-white/15"
              data-testid="desk-invite-email" autoFocus />
          </div>
          <div>
            <Label className="text-[10px] tracking-[0.22em] uppercase text-white/55">
              Mobile number
            </Label>
            <Input type="tel" value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="+91 98765 43210"
              className="mt-2 h-11 bg-black/40 border-white/15"
              data-testid="desk-invite-phone" />
            <div className="mt-1 text-[10px] text-white/45">
              At least one of email / mobile is required.
            </div>
          </div>
          <div>
            <Label className="text-[10px] tracking-[0.22em] uppercase text-white/55">
              Role
            </Label>
            <Select value={role} onValueChange={setRole}>
              <SelectTrigger className="mt-2 h-11 bg-black/40 border-white/15"
                data-testid="desk-invite-role-trigger">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#0a0a0c] border-white/15 text-white">
                <SelectItem value="programming_head">Programming Head</SelectItem>
                <SelectItem value="show_manager">Show Manager</SelectItem>
                <SelectItem value="creative_supervisor">Creative Supervisor</SelectItem>
                <SelectItem value="standards_and_practices">Standards & Practices</SelectItem>
                <SelectItem value="executive">Executive</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] tracking-[0.22em] uppercase text-white/55">
              Note (optional)
            </Label>
            <Textarea value={note} onChange={(e) => setNote(e.target.value)}
              rows={3}
              placeholder="Added to the invite email / SMS."
              className="mt-2 bg-black/40 border-white/15 text-sm"
              data-testid="desk-invite-note" />
          </div>
          <div className="flex items-center justify-end gap-2 pt-1">
            <Button variant="ghost" onClick={() => onOpenChange(false)}
              className="h-11 px-5 text-xs uppercase tracking-[0.22em]"
              data-testid="desk-invite-cancel">
              Cancel
            </Button>
            <Button onClick={submit}
              disabled={saving || (!email.trim() && !phone.trim())}
              className="btn-themed h-11 px-6 rounded-full text-xs uppercase tracking-[0.25em]"
              data-testid="desk-invite-submit">
              {saving ? "Inviting…" : "Invite"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function DecisionDialog({ action, onClose, deskId, onDecided }) {
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/show-desks/${deskId}/decision`, {
        action, note: note.trim(),
      });
      toast.success(action === "approve"
        ? "Desk approved — the channel team has access"
        : `Rejected. Channel is blocked until ${data.unblock_at?.slice(0, 10)}.`);
      onClose(); setNote("");
      onDecided?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not decide");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!action} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-[#0a0a0c] border border-white/10 text-white max-w-lg"
        data-testid="desk-decision-dialog">
        <DialogHeader>
          <DialogTitle className="font-display uppercase tracking-tight text-lg">
            {action === "approve" ? "Approve Show Desk" : "Reject Show Desk"}
          </DialogTitle>
          <DialogDescription className="text-white/55 text-xs">
            {action === "approve"
              ? "The channel programming team will gain scoped read access to this show."
              : "The channel will be blocked from re-requesting this show for 30 days."}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 mt-2">
          <div>
            <Label className="text-[10px] tracking-[0.22em] uppercase text-white/55">
              Note (optional)
            </Label>
            <Textarea value={note} onChange={(e) => setNote(e.target.value)}
              rows={3}
              placeholder={action === "approve"
                ? "Any welcome context you'd like the channel to see."
                : "Reason for rejection. Shared with the channel."}
              className="mt-2 bg-black/40 border-white/15 text-sm"
              data-testid="desk-decision-note" />
          </div>
          <div className="flex items-center justify-end gap-2 pt-1">
            <Button variant="ghost" onClick={onClose}
              className="h-11 px-5 text-xs uppercase tracking-[0.22em]"
              data-testid="desk-decision-cancel">
              Cancel
            </Button>
            <Button onClick={submit} disabled={saving}
              className={action === "approve"
                ? "btn-themed h-11 px-6 rounded-full text-xs uppercase tracking-[0.25em]"
                : "h-11 px-6 rounded-full text-xs uppercase tracking-[0.25em] bg-red-500/80 hover:bg-red-500 text-white"}
              data-testid="desk-decision-submit">
              {saving ? "Saving…" : action === "approve" ? "Approve" : "Reject + block 30 days"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
